import { FC, useCallback } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import { useLocation } from 'react-router';

import { safeUrl, techSearchParams } from '#/service/utils';

const BlankSafeLink: Components['a'] = ({
  href = '',
  children,
  node: _node,
  ...rest
}) => (
  <a {...rest} href={safeUrl(href)} rel="noopener noreferrer" target="_blank">
    {children}
  </a>
);

BlankSafeLink.displayName = 'BlankSafeLink';

interface MarkdownProps {
  children: string;
  className?: string;
}

/**
 * Our main Markdown component.
 * Sanitizes links, makes them open on a new tab, and handles internal navigation links.
 * This should always be used instead of `react-markdown` directly.
 * Every other markdown component should build on top of this one.
 * @param children The markdown content to render.
 * @param className Optional class name for styling.
 */
export const Markdown: FC<MarkdownProps> = ({ children, className }) => {
  const location = useLocation();

  const handleInternalNavigation = useCallback(
    (uri: string) => {
      // There will be a custom format for internal links.
      // See: https://accedobroadband.jira.com/browse/ONEWEB-2142?focusedCommentId=1357018
      // In the meantime, we just hardcode this one, because HMN is already using it.
      if (uri === 'accedooneca:TERMS_AND_CONDITIONS') {
        const searchParams = techSearchParams(location.search);
        const queryString = searchParams ? '?' + searchParams : '';
        uri = `/readme/terms${queryString}`;
      }

      return safeUrl(uri);
    },
    [location.search]
  );

  return (
    <ReactMarkdown
      className={className}
      components={{ a: BlankSafeLink }}
      linkTarget="_blank"
      transformLinkUri={handleInternalNavigation}
    >
      {children}
    </ReactMarkdown>
  );
};

Markdown.displayName = 'Markdown';
